/**
 * SpeedTestGauges.tsx
 *
 * Live download/upload gauges for the LibreSpeed quick test
 * (flow `sys_quick_speedtest_test_flow`).
 *
 * While the test is running, the active gauge is driven by the live sample
 * streamed from the edge. The edge derives `liveSample.phase` from an
 * INSTANTANEOUS tx-vs-rx byte-delta comparison, which flickers during
 * low-traffic moments (server setup, ping/jitter, brief stream gaps, ACK
 * bursts) — so the raw phase is NOT trustworthy frame-to-frame. We stabilise it
 * here:
 *   - a low noise floor drops setup/ping noise but keeps real slow-link traffic
 *     (an IoT SIM on roaming LTE can upload at ~0.3 Mbps);
 *   - the "Measuring download/upload…" label only flips after several
 *     consecutive meaningful samples agree (hysteresis), so it doesn't oscillate;
 *   - each gauge HOLDS its last observed value instead of dropping to 0 when the
 *     other direction reports.
 * When the run completes, the final download/upload speeds win.
 *
 * The gauge scale is dynamic — `niceMax` rounds up to the next sensible round
 * number above the highest speed observed (floor 50 Mbps) so the needle never
 * pegs, even on a ~1 Gbps 5G link.
 */

import React from 'react';
import { SpeedGauge } from '@/components/quick-tests/SpeedGauge';
import { SpeedTestChart, type SpeedSamplePoint } from '@/components/quick-tests/SpeedTestChart';

export interface SpeedTestLiveSample {
  phase: 'download' | 'upload';
  mbps: number;
  t?: string;
  commandId?: string;
}

export interface SpeedTestGaugesProps {
  liveSample?: SpeedTestLiveSample;
  downloadMbps?: number;
  uploadMbps?: number;
  pingMs?: number;
  jitterMs?: number;
  isLive: boolean;
  // Final-result extras (edge v4.4.0+). All optional — older executions and
  // pre-v4.4.0 modems simply don't render the chips / chart.
  peakDownloadMbps?: number;
  peakUploadMbps?: number;
  steadyDownloadMbps?: number;
  steadyUploadMbps?: number;
  packetLossPct?: number;
  idleLatencyMs?: number;
  downloadLatencyMs?: number;
  uploadLatencyMs?: number;
  bufferbloatMs?: number;
  downloadSamples?: SpeedSamplePoint[];
  uploadSamples?: SpeedSamplePoint[];
}

/** Small labelled stat chip for the finished-result strip. */
const StatChip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-md border border-border bg-muted/40 px-3 py-1.5 text-center">
    <div className="text-sm font-semibold text-foreground">{value}</div>
    <div className="text-[11px] text-muted-foreground/70">{label}</div>
  </div>
);

// Speed gauges read low→high = bad→good, so invert the default red→green ramp.
const SPEED_COLOR_RANGES = [
  { threshold: 25, color: '#ef4444' }, // Red — slow
  { threshold: 60, color: '#f59e0b' }, // Orange — moderate
  { threshold: 100, color: '#10b981' }, // Green — fast
];

// Below this, a sample is setup/ping noise — it must not move the gauges or the
// phase label. Kept low (0.1 Mbps) so genuinely slow real transfers (e.g. a
// ~0.3 Mbps roaming-LTE upload) still register.
const NOISE_FLOOR_MBPS = 0.1;
// Consecutive meaningful samples of a NEW direction required before the label
// flips. At ~0.4 s/sample this is ~1.2 s of sustained traffic — far shorter than
// a real phase (~10 s each) but long enough to ignore ACK-burst transients.
const PHASE_SWITCH_SAMPLES = 3;

/**
 * Round a peak speed up to the next "nice" round number for the gauge max.
 * Floor of 50 Mbps; steps grow with magnitude so a 1 Gbps link gets a sane
 * 1000-ish ceiling rather than 50 tiny increments.
 */
function niceMax(peak: number): number {
  const floor = 50;
  if (!Number.isFinite(peak) || peak <= 0) return floor;
  const target = Math.max(peak, floor) * 1.1; // 10% headroom above the peak
  let step = 50;
  if (target > 800) step = 200;
  else if (target > 400) step = 100;
  else if (target > 150) step = 50;
  else step = 25;
  return Math.max(floor, Math.ceil(target / step) * step);
}

function round1(n: number | undefined): number {
  if (n === undefined || !Number.isFinite(n)) return 0;
  return Math.round(n * 10) / 10;
}

export const SpeedTestGauges: React.FC<SpeedTestGaugesProps> = ({
  liveSample,
  downloadMbps,
  uploadMbps,
  pingMs,
  jitterMs,
  isLive,
  peakDownloadMbps,
  peakUploadMbps,
  steadyDownloadMbps,
  steadyUploadMbps,
  packetLossPct,
  idleLatencyMs,
  downloadLatencyMs,
  uploadLatencyMs,
  bufferbloatMs,
  downloadSamples,
  uploadSamples,
}) => {
  // Once the run completes the final download/upload values are present; the
  // (now stale) live sample is ignored so a leftover mid-test reading can't
  // override the authoritative result.
  const finalPresent = downloadMbps !== undefined || uploadMbps !== undefined;

  // Live hysteresis state. Refs accumulate across streamed samples; `force`
  // re-renders when they move. Held values persist so the inactive gauge keeps
  // its last reading rather than snapping to 0.
  const heldDl = React.useRef(0);
  const heldUl = React.useRef(0);
  const peakRef = React.useRef(0);
  const displayPhase = React.useRef<'download' | 'upload'>('download');
  const pendingPhase = React.useRef<'download' | 'upload' | null>(null);
  const pendingCount = React.useRef(0);
  const runKey = React.useRef<string | undefined>(undefined);
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  React.useEffect(() => {
    if (!isLive || finalPresent || !liveSample) return;
    // New run → reset all accumulators (commandId changes per allocation).
    if (liveSample.commandId !== runKey.current) {
      runKey.current = liveSample.commandId;
      heldDl.current = 0;
      heldUl.current = 0;
      peakRef.current = 0;
      displayPhase.current = liveSample.phase === 'upload' ? 'upload' : 'download';
      pendingPhase.current = null;
      pendingCount.current = 0;
    }
    const mbps = Number.isFinite(liveSample.mbps) ? liveSample.mbps : 0;
    if (mbps < NOISE_FLOOR_MBPS) return; // setup/ping noise — ignore entirely
    const phase: 'download' | 'upload' = liveSample.phase === 'upload' ? 'upload' : 'download';

    // Route the value by the sample's own direction (the edge computes mbps from
    // that direction's byte delta), holding the value across the other phase.
    if (phase === 'download') heldDl.current = mbps;
    else heldUl.current = mbps;

    // Label hysteresis: require PHASE_SWITCH_SAMPLES consecutive meaningful
    // samples of a new direction before flipping the displayed phase.
    if (phase === displayPhase.current) {
      pendingPhase.current = null;
      pendingCount.current = 0;
    } else if (phase === pendingPhase.current) {
      pendingCount.current += 1;
      if (pendingCount.current >= PHASE_SWITCH_SAMPLES) {
        displayPhase.current = phase;
        pendingPhase.current = null;
        pendingCount.current = 0;
      }
    } else {
      pendingPhase.current = phase;
      pendingCount.current = 1;
    }

    peakRef.current = Math.max(peakRef.current, heldDl.current, heldUl.current);
    force();
  }, [liveSample, isLive, finalPresent]);

  // Final results win once present; otherwise show the held live values.
  const dlValue = round1(finalPresent ? downloadMbps : heldDl.current);
  const ulValue = round1(finalPresent ? uploadMbps : heldUl.current);

  // Monotonic shared scale: only grows so the needle scale doesn't jitter.
  const max = niceMax(
    finalPresent ? Math.max(dlValue, ulValue) : Math.max(peakRef.current, dlValue, ulValue)
  );

  const showLive = isLive && !finalPresent && !!liveSample;
  const phaseLabel = displayPhase.current === 'download' ? 'Measuring download…' : 'Measuring upload…';
  const showPingJitter = pingMs !== undefined || jitterMs !== undefined;

  // Finished-result extras. Loaded latency = the worse of the two phase RTTs.
  const loadedVals = [downloadLatencyMs, uploadLatencyMs].filter(
    (v): v is number => typeof v === 'number'
  );
  const hasLoadedLatency = loadedVals.length > 0;
  const loadedLatencyMs = hasLoadedLatency ? Math.max(...loadedVals) : NaN;
  const chips: Array<{ label: string; value: string }> = [];
  if (finalPresent) {
    if (typeof peakDownloadMbps === 'number') chips.push({ label: 'Peak ↓', value: `${round1(peakDownloadMbps)} Mbps` });
    if (typeof peakUploadMbps === 'number') chips.push({ label: 'Peak ↑', value: `${round1(peakUploadMbps)} Mbps` });
    if (typeof steadyDownloadMbps === 'number') chips.push({ label: 'Steady ↓', value: `${round1(steadyDownloadMbps)} Mbps` });
    if (typeof steadyUploadMbps === 'number') chips.push({ label: 'Steady ↑', value: `${round1(steadyUploadMbps)} Mbps` });
    if (typeof packetLossPct === 'number') chips.push({ label: 'Packet loss', value: `${round1(packetLossPct)}%` });
    if (typeof idleLatencyMs === 'number' && hasLoadedLatency) {
      const bloat = typeof bufferbloatMs === 'number' ? bufferbloatMs : Math.max(0, loadedLatencyMs - idleLatencyMs);
      chips.push({ label: 'Idle → loaded latency', value: `${round1(idleLatencyMs)} → ${round1(loadedLatencyMs)} ms (+${round1(bloat)})` });
    }
  }

  return (
    <div className="rounded border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-foreground">Speed Test</h3>
        {showLive && (
          <span className="text-xs text-muted-foreground/70">
            {phaseLabel}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SpeedGauge value={dlValue} max={max} label="Download" unit=" Mbps" colorRanges={SPEED_COLOR_RANGES} animationDelayMs={0} />
        <SpeedGauge value={ulValue} max={max} label="Upload" unit=" Mbps" colorRanges={SPEED_COLOR_RANGES} animationDelayMs={80} />
      </div>

      {showPingJitter && (
        <div className="mt-3 flex justify-center gap-8 border-t border-border pt-3">
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{round1(pingMs)} ms</div>
            <div className="text-xs text-muted-foreground/70">Ping</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-foreground">{round1(jitterMs)} ms</div>
            <div className="text-xs text-muted-foreground/70">Jitter</div>
          </div>
        </div>
      )}

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap justify-center gap-2 border-t border-border pt-3">
          {chips.map((c) => (
            <StatChip key={c.label} label={c.label} value={c.value} />
          ))}
        </div>
      )}

      {finalPresent && (
        <SpeedTestChart
          downloadSamples={downloadSamples}
          uploadSamples={uploadSamples}
          peakDownloadMbps={peakDownloadMbps}
          steadyDownloadMbps={steadyDownloadMbps}
        />
      )}
    </div>
  );
};
